import React from "react";
import { useNavigate } from "react-router-dom";
import PublicHeader from "../components/homepage/public/PublicHeader";
import PublicFooter from "../components/homepage/public/PublicFooter";

const IMG_BASE = "/images";

const styles = {
  page: {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    background: "#fff",
    color: "#0f172a",
  },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "0 20px" },

  hero: {
    padding: "54px 0",
    background: "radial-gradient(circle at top, #f2f4fa 0, #ffffff 62%)",
    borderBottom: "1px solid #e2e8f0",
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1.1fr) minmax(0,0.9fr)",
    gap: "34px",
    alignItems: "center",
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "42px",
    lineHeight: 1.06,
    margin: 0,
    color: "#020617",
  },
  heroText: { marginTop: "14px", fontSize: "15px", color: "#475569", lineHeight: 1.9 },
  heroActions: { marginTop: "18px", display: "flex", gap: "10px", flexWrap: "wrap" },
  btn: {
    fontSize: "13px",
    padding: "10px 18px",
    borderRadius: "999px",
    border: "1px solid #d0d7e2",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #1d4ed8, #0b1530)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 10px 25px rgba(15,23,42,0.14)",
  },
  heroCard: {
    borderRadius: "22px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
    overflow: "hidden",
  },
  heroImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },

  section: { padding: "70px 0" },
  sectionAlt: {
    padding: "70px 0",
    background: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
  },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: "28px", margin: "0 0 10px", color: "#020617" },
  p: { fontSize: "14px", color: "#64748b", lineHeight: 1.9, margin: 0 },

  cards: {
    marginTop: "26px",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0,1fr))",
    gap: "16px",
  },
  card: {
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "18px",
    boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
    cursor: "pointer",
  },
  cardDisabled: { opacity: 0.65, cursor: "not-allowed" },
  cardTitleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" },
  cardTitle: { fontSize: "16px", fontWeight: 800, margin: 0, color: "#0f172a" },
  badge: {
    fontSize: "11px",
    padding: "4px 10px",
    borderRadius: "999px",
    background: "rgba(29,78,216,0.10)",
    color: "#1d4ed8",
    fontWeight: 800,
  },
  cardText: { marginTop: "10px", fontSize: "13px", color: "#64748b", lineHeight: 1.8 },

  whyGrid: { marginTop: "22px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "14px" },
  why: { borderRadius: "18px", border: "1px solid #e2e8f0", background: "#fff", padding: "16px" },
  whyTitle: { fontWeight: 800, margin: 0, fontSize: "14px" },
  whyText: { marginTop: "6px", fontSize: "13px", color: "#64748b", lineHeight: 1.7 },

  ctaBox: {
    borderRadius: "22px",
    border: "1px solid #e2e8f0",
    background: "linear-gradient(180deg, #ffffff, #f8fafc)",
    padding: "22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  ctaTitle: { fontWeight: 900, margin: 0, fontSize: "16px" },
  ctaSub: { margin: "4px 0 0", color: "#64748b", fontSize: "13px" },
};

export default function TutoringMain() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <PublicHeader />

      <section style={styles.hero}>
        <div style={styles.container}>
          <div style={styles.heroGrid}>
            <div>
              <h1 style={styles.heroTitle}>True Education Tutoring Programs</h1>
              <p style={styles.heroText}>
                Clean, structured, and individualized tutoring designed to build confidence and results. We
                start with diagnostics, create a focused plan, and coach students step-by-step—exactly like a
                professional learning platform.
              </p>
              <div style={styles.heroActions}>
                <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => navigate("/tutoring/sat")}>
                  Explore SAT Tutoring
                </button>
                <button style={styles.btn} onClick={() => navigate("/login")}>
                  Login
                </button>
              </div>
            </div>

            <div style={styles.heroCard}>
              <img
                src={`${IMG_BASE}/tutoring/tutor-hero.jpg`}
                alt="Professional tutoring"
                style={styles.heroImg}
              />
              <div style={{ padding: "16px" }}>
                <div style={{ fontWeight: 900 }}>Professional. Clear. Results-driven.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.container}>
          <h2 style={styles.h2}>About Our Tutoring</h2>
          <p style={styles.p}>
            True Education tutoring is built around one goal: maximize learning efficiency. We don’t waste time
            reviewing what students already know—we diagnose first, then teach only what moves the score.
          </p>
        </div>
      </section>

      <section style={styles.sectionAlt}>
        <div style={styles.container}>
          <h2 style={styles.h2}>What We Offer</h2>
          <p style={styles.p}>Choose a program below. SAT is our flagship, fully-developed page.</p>

          <div style={styles.cards}>
            <div style={styles.card} onClick={() => navigate("/tutoring/sat")}>
              <div style={styles.cardTitleRow}>
                <h3 style={styles.cardTitle}>SAT Tutoring</h3>
                <span style={styles.badge}>Featured</span>
              </div>
              <div style={styles.cardText}>
                Reading & Writing + Math, diagnostics, personalized plans, and accurate progress tracking.
              </div>
            </div>

            <div style={{ ...styles.card, ...styles.cardDisabled }}>
              <div style={styles.cardTitleRow}>
                <h3 style={styles.cardTitle}>AP Tutoring</h3>
                <span style={styles.badge}>Coming soon</span>
              </div>
              <div style={styles.cardText}>
                AP Calculus, AP Biology, AP Physics, AP Lang, and more—page will be built after SAT.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.container}>
          <h2 style={styles.h2}>Why Choose True Education</h2>
          <p style={styles.p}>Same core pillars as your “Key Qualities” mockup.</p>

          <div style={styles.whyGrid}>
            <div style={styles.why}>
              <p style={styles.whyTitle}>Diagnostic-first</p>
              <p style={styles.whyText}>We identify weak skills quickly and target high-yield improvements.</p>
            </div>
            <div style={styles.why}>
              <p style={styles.whyTitle}>Personalized plan</p>
              <p style={styles.whyText}>A realistic weekly roadmap that covers everything needed—with no fluff.</p>
            </div>
            <div style={styles.why}>
              <p style={styles.whyTitle}>Individualized teaching</p>
              <p style={styles.whyText}>1-on-1 coaching, strategy, and test-day mindset support.</p>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.sectionAlt}>
        <div style={styles.container}>
          <div style={styles.ctaBox}>
            <div>
              <p style={styles.ctaTitle}>Ready to start?</p>
              <p style={styles.ctaSub}>Book a free consultation or explore the SAT program details.</p>
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button style={styles.btn} onClick={() => (window.location.href = "mailto:cumming@trueeducation.com")}>
                Contact Us
              </button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => navigate("/tutoring/sat")}>
                SAT Tutoring →
              </button>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
